import { classNames } from '@panwds/tailwindcss';
import React, { forwardRef, useRef } from 'react';
import { mergeRefs } from '../../utils/refs/refs.esm.js';
import '@popperjs/core';
import '../Button/Button.esm.js';
import '@panwds/icons';
import '../Icon/ImgIcon.esm.js';
import { SuccessIcon, DangerIcon } from '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import { CopyButton } from '../Button/CopyButton.esm.js';
import { ClearButton } from './ClearButton.esm.js';

const defaultProps = {
    allowCopy: false,
    danger: false,
    disabled: false,
    readOnly: false,
    success: false,
    type: 'text'
};
/**
 * Lets users enter and edit text.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Input } from "@panwds/react-ui";
 * ```
 */
const Input = forwardRef(({ addClassName, allowClear, allowCopy, appearance, dataMetrics, dataTestId, danger, disabled, onKeyDown, onChange, placeholder, prefix, readOnly, success, suffix, value, type, ...passThrough }, userRef) => {
    const isControlled = useRef(value !== undefined);
    const internalRef = useRef();
    const ref = mergeRefs([userRef, internalRef]);
    const hasError = danger || appearance === 'error';
    const isSuccess = success || appearance === 'success';
    const borderColor = isSuccess
        ? 'tw-border-teal-700'
        : hasError
            ? 'tw-border-red-700 dark:tw-border-red-400'
            : readOnly
                ? 'tw-border-gray-100 dark:tw-border-gray-950'
                : disabled
                    ? 'tw-border-gray-300 dark:tw-border-gray-950'
                    : 'tw-border-gray-300 dark:tw-border-gray-950';
    const rootClassName = classNames('tw-border-solid tw-flex tw-items-center tw-space-x-1 tw-px-2 tw-py-1 tw-h-8 tw-border tw-rounded tw-box-border', borderColor, readOnly ? 'tw-bg-gray-100 dark:tw-bg-gray-1050' : 'tw-bg-white focus-within:tw-custom-ring dark:tw-bg-gray-1050', disabled && 'tw-text-disabled tw-cursor-not-allowed', addClassName);
    const inputClassName = classNames('tw-font-lato tw-border-0 focus:tw-outline-none tw-placeholder-gray-300 dark:tw-placeholder-gray-400 dark:tw-text-dark-bg tw-flex-auto tw-bg-transparent tw-text-xs', disabled ? 'tw-cursor-not-allowed tw-text-disabled dark:tw-text-gray-600' : 'tw-text-default', readOnly && 'dark:tw-text-gray-100');
    return (React.createElement("span", { className: rootClassName },
        prefix,
        React.createElement("input", { ref: ref, className: inputClassName, "data-metrics": dataMetrics, "data-testid": dataTestId || 'input', disabled: disabled, onChange: onChange, onKeyDown: onKeyDown, placeholder: placeholder, value: value, readOnly: readOnly, type: type, ...passThrough }),
        suffix,
        allowClear && (React.createElement(ClearButton, { addClassName: "p-0.5", style: { marginRight: '-0.125rem' }, onClick: (e) => {
                var _a;
                if (isControlled.current) {
                    if (internalRef.current && onChange) {
                        onChange(makeMockClearEvent(internalRef.current, e));
                    }
                }
                else {
                    (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.setRangeText('', 0, Number.MAX_SAFE_INTEGER);
                }
            } })),
        allowCopy && (React.createElement(CopyButton, { addClassName: "tw-p-0.5", style: { marginRight: '-0.125rem' }, text: () => { var _a; return (_a = internalRef.current) === null || _a === void 0 ? void 0 : _a.value; } })),
        isSuccess && React.createElement(SuccessIcon, null),
        hasError && React.createElement(DangerIcon, { "aria-hidden": true })));
});
function makeMockClearEvent(inputRef, clickEvent) {
    const updatedTarget = { ...inputRef, value: '' };
    const { nativeEvent, bubbles, cancelable, defaultPrevented, eventPhase, isTrusted, preventDefault, isDefaultPrevented, stopPropagation, isPropagationStopped, persist, timeStamp } = clickEvent;
    return {
        currentTarget: updatedTarget,
        target: updatedTarget,
        nativeEvent,
        bubbles,
        cancelable,
        defaultPrevented,
        eventPhase,
        isTrusted,
        preventDefault,
        isDefaultPrevented,
        stopPropagation,
        isPropagationStopped,
        persist,
        timeStamp,
        type: 'clear'
    };
}
Input.displayName = 'Input';
Input.defaultProps = defaultProps;

export { Input };
//# sourceMappingURL=Input.esm.js.map
